import React, { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Loader2 } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { queryKeys } from '@/queries/keys';
import webhooksService, { WebhookConfig, CreateWebhookRequest, UpdateWebhookRequest } from '@/lib/api/webhooks';

interface WebhookFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  webhook?: WebhookConfig;
  defaultName?: string;
  instructions?: string;
  onSuccess?: () => void;
}

interface FormState {
  name: string;
  url: string;
  secret: string;
  notify_on_qualified: boolean;
  notify_on_booked: boolean;
  notify_on_active_session: boolean;
}

const initialFormState: FormState = {
  name: '',
  url: '',
  secret: '',
  notify_on_qualified: true,
  notify_on_booked: true,
  notify_on_active_session: false,
};

const WebhookFormDialog: React.FC<WebhookFormDialogProps> = ({
  open,
  onOpenChange,
  organizationId,
  webhook,
  defaultName,
  instructions,
  onSuccess,
}) => {
  const queryClient = useQueryClient();
  const isEditing = !!webhook;

  const [form, setForm] = useState<FormState>(initialFormState);
  const [urlError, setUrlError] = useState('');

  useEffect(() => {
    if (!open) return;
    if (webhook) {
      setForm({
        name: webhook.name || '',
        url: webhook.url,
        secret: '',
        notify_on_qualified: webhook.notify_on_qualified,
        notify_on_booked: webhook.notify_on_booked,
        notify_on_active_session: webhook.notify_on_active_session,
      });
    } else {
      setForm({ ...initialFormState, name: defaultName || '' });
    }
    setUrlError('');
  }, [open, webhook, defaultName]);

  const createMutation = useMutation({
    mutationFn: async (data: CreateWebhookRequest) => {
      const response = await webhooksService.createWebhook(organizationId, data);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to create webhook.');
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.webhooks.list(organizationId) });
      toast({ title: 'Webhook created', description: 'The webhook has been created successfully.' });
      onOpenChange(false);
      onSuccess?.();
    },
    onError: (error) => {
      toast({
        title: 'Error creating webhook',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: UpdateWebhookRequest) => {
      const response = await webhooksService.updateWebhook(webhook!.id, data);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to update webhook.');
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.webhooks.list(organizationId) });
      toast({ title: 'Webhook updated', description: 'The webhook has been updated successfully.' });
      onOpenChange(false);
      onSuccess?.();
    },
    onError: (error) => {
      toast({
        title: 'Error updating webhook',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    },
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  const validate = (): boolean => {
    if (!form.url.trim()) {
      setUrlError('URL is required.');
      return false;
    }
    if (!form.url.startsWith('https://')) {
      setUrlError('URL must start with https://');
      return false;
    }
    setUrlError('');
    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      name: form.name.trim() || undefined,
      url: form.url.trim(),
      secret: form.secret.trim() || undefined,
      notify_on_qualified: form.notify_on_qualified,
      notify_on_booked: form.notify_on_booked,
      notify_on_active_session: form.notify_on_active_session,
    };

    if (isEditing) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Webhook' : 'Create Webhook'}</DialogTitle>
          <DialogDescription>
            {instructions || 'Configure a webhook endpoint to receive event notifications.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="webhook-name">Name (optional)</Label>
            <Input
              id="webhook-name"
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="e.g. HubSpot, Salesforce"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="webhook-url">URL</Label>
            <Input
              id="webhook-url"
              value={form.url}
              onChange={(e) => {
                setForm((prev) => ({ ...prev, url: e.target.value }));
                if (urlError) setUrlError('');
              }}
              placeholder="https://example.com/webhook"
            />
            {urlError && <p className="text-xs text-destructive">{urlError}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="webhook-secret">Signing secret (optional)</Label>
            <Input
              id="webhook-secret"
              type="password"
              value={form.secret}
              onChange={(e) => setForm((prev) => ({ ...prev, secret: e.target.value }))}
              placeholder={isEditing ? 'Leave blank to keep current' : 'Optional signing secret'}
            />
          </div>

          <div className="space-y-3 pt-2">
            <Label className="text-sm font-medium">Notification filters</Label>
            {[
              { key: 'notify_on_qualified' as const, label: 'Qualified leads' },
              { key: 'notify_on_booked' as const, label: 'Meetings booked' },
              { key: 'notify_on_active_session' as const, label: 'Active sessions' },
            ].map(({ key, label }) => (
              <div key={key} className="flex items-center justify-between">
                <Label htmlFor={`webhook-${key}`} className="text-sm">{label}</Label>
                <Switch
                  id={`webhook-${key}`}
                  checked={form[key]}
                  onCheckedChange={(checked) => setForm((prev) => ({ ...prev, [key]: checked }))}
                />
              </div>
            ))}
          </div>

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {isEditing ? 'Saving...' : 'Creating...'}
                </span>
              ) : (
                isEditing ? 'Save Changes' : 'Create Webhook'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default WebhookFormDialog;
